import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { BarChart3, Users, Cpu, Download, Calendar, Filter, TrendingUp, DollarSign, Settings, ClipboardList } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import PracticeSessionsTab from './PracticeSessionsTab';

const TYPE_LABELS = {
  simulation_911: 'Simulation 911',
  rao_variant: 'Variante RAO',
  retroaction: 'Rétroaction',
};

const TYPE_COLORS = {
  simulation_911: '#2E6CF5',
  rao_variant: '#24B219',
  retroaction: '#F4621C',
};

const UsageTab = () => {
  const [summary, setSummary] = useState(null);
  const [byStudent, setByStudent] = useState([]);
  const [period, setPeriod] = useState(30);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [subTab, setSubTab] = useState('global');
  const [groups, setGroups] = useState([]);
  const [costPer1kTokens, setCostPer1kTokens] = useState(() => {
    const saved = localStorage.getItem('llm_cost_per_1k');
    return saved ? parseFloat(saved) : 0.01;
  });
  const [showCostConfig, setShowCostConfig] = useState(false);

  const saveCostConfig = (val) => {
    const num = parseFloat(val) || 0;
    setCostPer1kTokens(num);
    localStorage.setItem('llm_cost_per_1k', num.toString());
  };

  const estimateCost = (tokens) => ((tokens / 1000) * costPer1kTokens).toFixed(2);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [sumResp, studResp] = await Promise.all([
        axios.get(`${API}/admin/usage/summary?days=${period}`),
        axios.get(`${API}/admin/usage/by-student?days=${period}`),
      ]);
      setSummary(sumResp.data);
      setByStudent(studResp.data);
    } catch (err) {
      console.error('Error loading usage data:', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    axios.get(`${API}/rao/admin/groups`).then(r => setGroups(r.data || [])).catch(() => {});
  }, []);

  const exportCSV = async () => {
    try {
      const resp = await axios.get(`${API}/admin/usage/export?days=${period}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `utilisation_llm_${period}j.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export CSV téléchargé');
    } catch {
      toast.error("Erreur lors de l'export");
    }
  };

  const maxCalls = summary?.daily?.length
    ? Math.max(...summary.daily.map(d => d.calls), 1)
    : 1;

  const filteredStudents = filterType === 'all'
    ? byStudent
    : byStudent.filter(s => s[filterType] > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-pulse text-lg" style={{ color: '#2E6CF5' }}>Chargement des données...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Sub-tab switcher */}
      <div className="flex items-center gap-2 mb-6 border-b-2 pb-2" style={{ borderColor: '#E5E7EB' }}>
        <button
          onClick={() => setSubTab('global')}
          className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium text-sm transition-all ${
            subTab === 'global' ? 'text-white' : 'text-gray-500 hover:text-gray-700'
          }`}
          style={subTab === 'global' ? { backgroundColor: '#002768' } : {}}
          data-testid="subtab-global"
        >
          <BarChart3 className="w-4 h-4" />
          Vue globale
        </button>
        <button
          onClick={() => setSubTab('practice')}
          className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium text-sm transition-all ${
            subTab === 'practice' ? 'text-white' : 'text-gray-500 hover:text-gray-700'
          }`}
          style={subTab === 'practice' ? { backgroundColor: '#F4621C' } : {}}
          data-testid="subtab-practice"
        >
          <ClipboardList className="w-4 h-4" />
          Séances de pratique
        </button>
      </div>

      {subTab === 'practice' ? (
        <PracticeSessionsTab costPer1kTokens={costPer1kTokens} groups={groups} />
      ) : (
      <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h2 className="text-2xl font-bold" style={{ color: '#002768' }} data-testid="usage-title">
          Utilisation LLM
        </h2>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {[7, 30, 90].map(d => (
              <button
                key={d}
                onClick={() => setPeriod(d)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  period === d ? 'text-white' : 'text-gray-600 hover:bg-white'
                }`}
                style={period === d ? { backgroundColor: '#002768' } : {}}
                data-testid={`period-${d}`}
              >
                {d}j
              </button>
            ))}
          </div>
          <button
            onClick={exportCSV}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-white font-medium text-sm hover:opacity-90"
            style={{ backgroundColor: '#24B219' }}
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={() => setShowCostConfig(!showCostConfig)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium text-sm border-2 transition-colors"
            style={{ borderColor: '#F4621C', color: '#F4621C' }}
            data-testid="cost-config-btn"
          >
            <Settings className="w-4 h-4" />
            Coût
          </button>
        </div>
      </div>

      {/* Cost Config */}
      {showCostConfig && (
        <div className="mb-6 p-4 rounded-xl border-2 bg-orange-50" style={{ borderColor: '#F4621C' }} data-testid="cost-config-panel">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" style={{ color: '#F4621C' }} />
              <span className="text-sm font-medium">Coût par 1 000 tokens ($ CAD) :</span>
            </div>
            <input
              type="number"
              step="0.001"
              min="0"
              value={costPer1kTokens}
              onChange={(e) => saveCostConfig(e.target.value)}
              className="w-28 px-3 py-1.5 border-2 rounded-lg text-sm font-mono focus:outline-none focus:ring-2"
              style={{ borderColor: '#F4621C', '--tw-ring-color': '#F4621C' }}
              data-testid="cost-input"
            />
            <span className="text-xs text-gray-500">
              Ajustez selon votre facturation Emergent. Ce paramètre est sauvegardé localement.
            </span>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Card className="glass border-0">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total appels LLM</p>
                <p className="text-3xl font-bold" style={{ color: '#002768' }} data-testid="total-calls">
                  {summary?.total_calls || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#CAE9FA' }}>
                <Cpu className="w-6 h-6" style={{ color: '#002768' }} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-0">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Tokens estimés</p>
                <p className="text-3xl font-bold" style={{ color: '#2E6CF5' }} data-testid="total-tokens">
                  {(summary?.total_tokens || 0).toLocaleString('fr-FR')}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#E0F8E0' }}>
                <TrendingUp className="w-6 h-6" style={{ color: '#24B219' }} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-0">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Étudiants actifs</p>
                <p className="text-3xl font-bold" style={{ color: '#F4621C' }} data-testid="active-students">
                  {byStudent.filter(s => s.student_number !== 'inconnu').length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#FFF3E0' }}>
                <Users className="w-6 h-6" style={{ color: '#F4621C' }} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-0">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Moy. / étudiant</p>
                <p className="text-3xl font-bold" style={{ color: '#002768' }} data-testid="avg-per-student">
                  {byStudent.filter(s => s.student_number !== 'inconnu').length > 0
                    ? Math.round((summary?.total_calls || 0) / byStudent.filter(s => s.student_number !== 'inconnu').length)
                    : 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#CAE9FA' }}>
                <BarChart3 className="w-6 h-6" style={{ color: '#2E6CF5' }} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-0" style={{ borderLeft: '3px solid #F4621C' }}>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Coût estimé</p>
                <p className="text-3xl font-bold" style={{ color: '#F4621C' }} data-testid="total-cost">
                  {estimateCost(summary?.total_tokens || 0)} $
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#FFF3E0' }}>
                <DollarSign className="w-6 h-6" style={{ color: '#F4621C' }} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="glass border-0">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Filter className="w-4 h-4" style={{ color: '#2E6CF5' }} />
              Répartition par type
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(summary?.by_type || {}).map(([type, data]) => {
                const pct = summary.total_calls > 0 ? (data.calls / summary.total_calls) * 100 : 0;
                return (
                  <div key={type}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">{TYPE_LABELS[type] || type}</span>
                      <span className="text-sm text-gray-500">
                        {data.calls} appels ({data.tokens.toLocaleString('fr-FR')} tokens)
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-3">
                      <div
                        className="h-3 rounded-full transition-all"
                        style={{ width: `${pct}%`, backgroundColor: TYPE_COLORS[type] || '#999' }}
                      />
                    </div>
                  </div>
                );
              })}
              {Object.keys(summary?.by_type || {}).length === 0 && (
                <p className="text-gray-400 text-center py-4">Aucune donnée pour cette période</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Daily Chart */}
        <Card className="glass border-0">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Calendar className="w-4 h-4" style={{ color: '#2E6CF5' }} />
              Appels par jour
            </CardTitle>
          </CardHeader>
          <CardContent>
            {summary?.daily?.length > 0 ? (
              <div className="flex items-end gap-1 h-40" data-testid="daily-chart">
                {summary.daily.slice(-Math.min(period, 30)).map((day, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                    <div
                      className="w-full rounded-t-sm transition-all hover:opacity-80 min-h-[2px]"
                      style={{
                        height: `${(day.calls / maxCalls) * 100}%`,
                        backgroundColor: '#2E6CF5',
                      }}
                    />
                    <div className="absolute -top-8 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                      {new Date(day.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} — {day.calls} appels
                    </div>
                    {(i === 0 || i === summary.daily.slice(-Math.min(period, 30)).length - 1) && (
                      <span className="text-[9px] text-gray-400 mt-1 truncate w-full text-center">
                        {new Date(day.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-center py-8">Aucune donnée pour cette période</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Per-Student Table */}
      <Card className="glass border-0">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="w-4 h-4" style={{ color: '#002768' }} />
              Utilisation par étudiant
            </CardTitle>
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              {[
                { id: 'all', label: 'Tous' },
                { id: 'simulation_911', label: 'Sim. 911' },
                { id: 'rao_variant', label: 'RAO' },
                { id: 'retroaction', label: 'Rétro.' },
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setFilterType(f.id)}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                    filterType === f.id ? 'text-white' : 'text-gray-600 hover:bg-white'
                  }`}
                  style={filterType === f.id ? { backgroundColor: TYPE_COLORS[f.id] || '#002768' } : {}}
                  data-testid={`filter-${f.id}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredStudents.length === 0 ? (
            <p className="text-gray-400 text-center py-8">Aucune donnée pour cette période</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="student-usage-table">
                <thead>
                  <tr className="border-b-2" style={{ borderColor: '#CAE9FA' }}>
                    <th className="text-left py-3 px-3 font-semibold text-gray-600">Étudiant</th>
                    <th className="text-left py-3 px-3 font-semibold text-gray-600"># Étudiant</th>
                    <th className="text-center py-3 px-3 font-semibold" style={{ color: '#2E6CF5' }}>Sim. 911</th>
                    <th className="text-center py-3 px-3 font-semibold" style={{ color: '#24B219' }}>RAO</th>
                    <th className="text-center py-3 px-3 font-semibold" style={{ color: '#F4621C' }}>Rétro.</th>
                    <th className="text-center py-3 px-3 font-semibold text-gray-600">Total</th>
                    <th className="text-center py-3 px-3 font-semibold text-gray-600">Tokens</th>
                    <th className="text-center py-3 px-3 font-semibold" style={{ color: '#F4621C' }}>Coût $</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map((s, i) => (
                    <tr key={s.student_number} className={`border-b ${i % 2 === 0 ? 'bg-gray-50/50' : ''}`}>
                      <td className="py-2.5 px-3 font-medium">{s.student_name}</td>
                      <td className="py-2.5 px-3 text-gray-500">{s.student_number}</td>
                      <td className="py-2.5 px-3 text-center">
                        {s.simulation_911 > 0 && (
                          <Badge className="text-white text-xs" style={{ backgroundColor: '#2E6CF5' }}>{s.simulation_911}</Badge>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        {s.rao_variant > 0 && (
                          <Badge className="text-white text-xs" style={{ backgroundColor: '#24B219' }}>{s.rao_variant}</Badge>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        {s.retroaction > 0 && (
                          <Badge className="text-white text-xs" style={{ backgroundColor: '#F4621C' }}>{s.retroaction}</Badge>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-center font-bold" style={{ color: '#002768' }}>{s.total_calls}</td>
                      <td className="py-2.5 px-3 text-center text-gray-500">{s.total_tokens.toLocaleString('fr-FR')}</td>
                      <td className="py-2.5 px-3 text-center font-semibold" style={{ color: '#F4621C' }}>{estimateCost(s.total_tokens)} $</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 font-bold" style={{ borderColor: '#002768' }}>
                    <td className="py-3 px-3" colSpan={2}>Total</td>
                    <td className="py-3 px-3 text-center" style={{ color: '#2E6CF5' }}>
                      {filteredStudents.reduce((a, s) => a + s.simulation_911, 0)}
                    </td>
                    <td className="py-3 px-3 text-center" style={{ color: '#24B219' }}>
                      {filteredStudents.reduce((a, s) => a + s.rao_variant, 0)}
                    </td>
                    <td className="py-3 px-3 text-center" style={{ color: '#F4621C' }}>
                      {filteredStudents.reduce((a, s) => a + s.retroaction, 0)}
                    </td>
                    <td className="py-3 px-3 text-center" style={{ color: '#002768' }}>
                      {filteredStudents.reduce((a, s) => a + s.total_calls, 0)}
                    </td>
                    <td className="py-3 px-3 text-center text-gray-500">
                      {filteredStudents.reduce((a, s) => a + s.total_tokens, 0).toLocaleString('fr-FR')}
                    </td>
                    <td className="py-3 px-3 text-center font-bold" style={{ color: '#F4621C' }}>
                      {estimateCost(filteredStudents.reduce((a, s) => a + s.total_tokens, 0))} $
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      </div>
      )}
    </div>
  );
};

export default UsageTab;
